import L, { LatLng, Layer, Map, marker, Marker, tileLayer } from "leaflet";
import { computeArea } from "spherical-geometry-js";
import {
  Feature,
  Polygon,
  Position,
  Properties,
  booleanContains,
  booleanPointInPolygon,
  booleanPointOnLine,
  booleanWithin,
  buffer,
  centroid,
  difference,
  intersect,
  lineIntersect,
  lineString,
  point,
  polygon as poly,
  polygon,
} from "@turf/turf";
import { samplingStationIcon } from "@/components/map/utils/customs";
import { Validation } from "@/features/map/domain/agromap.interface";
import { areaCalculation } from "@/features/map/utils/area-calculation";
import { productColors } from "@/features/map/utils/polygon-border-color";
import { IGeoShapeDetails } from "@/features/map/domain/IGeoShapeDetails";
import { CoordinateShape } from "@/features/map/presentation/pages/AgroMap";
import {
  baseConfigs,
  globalEditMode,
  persianTranslation,
} from "@/features/map/utils/configs";

export class AgroMap {
  map: Map | null = null;
  marker: Marker | undefined;
  private _updatedMarkerCoordinates: Position | undefined;
  polygonDetails: IGeoShapeDetails = {
    polygon: [],
    centerCoordinates: [],
    area: 0,
  };

  constructor(
    private popupRef: React.RefObject<HTMLDivElement>,
    protected toolbarOptions: L.PM.ToolbarOptions,
    protected zoomControl: boolean,
    private markerIcon: L.Icon<L.IconOptions> | L.DivIcon,
    private mapCenter?: Position
  ) {}

  init(mapId: string) {
    const container = L.DomUtil.get(`map${mapId}`);
    if (container != null) {
      (container as any)._leaflet_id = null;
    }
    this.map = new Map(`map${mapId}`, {
      center: this.mapCenter
        ? new LatLng(this.mapCenter[0], this.mapCenter[1])
        : new LatLng(baseConfigs.mapCenter[0], baseConfigs.mapCenter[1]),
      zoom: baseConfigs.zoom,
      zoomControl: this.zoomControl,
    });
    // ایونت کلیک که معمولا برای ایجاد مارکر استفاده می شود
    this.map
      .on("click", (event) => {
        this.methodWhenClickEventFired(event);
        // this.pinMarker(e?.latlng);
      })
      // ایونت ایجاد که معمولا برای ایجاد پلیگان یا خط استفاده می شود
      .on("pm:create", ({ layer }) => {
        this.map?.pm.Toolbar.setButtonDisabled("drawPolygon", true);
        this.map?.pm.Toolbar.setButtonDisabled("drawPolyline", true);
        this.methodWhenCreateEventFired(this.map!, layer);

        // ویرایش پلیگانی که خود کاربر با استفاده از تولبار رسم کرده است این ایونت را فعال می کند
        layer.on("pm:edit", ({ layer }) => {
          this.methodWhenEditEventFired(layer);
        });

        layer.on("pm:remove", (_) => {
          this.map?.pm.Toolbar.setButtonDisabled("drawPolygon", false);
        });
      })
      // این ایونت زمانی فعال می شود که شروع به رسم پلیگان کنیم
      .on("pm:drawstart", ({ workingLayer }) => {
        // به ازای افزودن هر نقطه به پلیگان فعال می شود.
        workingLayer.on("pm:vertexadded", (event) => {
          // Optimizing
          // this.methodWhenVertexAddEventFired(workingLayer, event, this.map!);
          // Optimizing
        });
      })
      .on("pm:drawend", (_) => {
        this.methodWhenDrawEnd(this.map!);
      });

    this.map.pm.setLang("fa", persianTranslation, "en");
    this.map.pm.addControls(this.toolbarOptions);
    tileLayer(baseConfigs.googleHybridUrl, {
      maxZoom: baseConfigs.maxZoom,
      attribution: baseConfigs.attribution,
      subdomains: baseConfigs.subdomains,
    }).addTo(this.map);
  }

  initEditListeners() {
    this.map!.pm.getGeomanLayers()?.forEach((layer) => {
      layer.on("pm:edit", (e) => {
        this.methodWhenEditEventFired(layer);
      });

      layer.on("pm:markerdrag", (event) => {
        this.methodWhenMarkerDrag(event.markerEvent.target._latlng, layer);
      });
    });
  }
  methodWhenClickEventFired(event: L.LeafletMouseEvent) {}
  methodWhenCreateEventFired(map: Map, layer: L.Layer) {}
  methodWhenEditEventFired(layer: L.Layer) {}
  methodWhenVertexAddEventFired(
    workingLayer: L.Layer,
    event: {
      shape: L.PM.SUPPORTED_SHAPES;
      workingLayer: L.Layer;
      marker: L.Marker;
      latlng: L.LatLng;
    },
    map: Map
  ) {}
  methodWhenDrawEnd(map: Map) {}
  methodWhenMarkerDrag(_latlng: LatLng, layer: L.Layer) {}

  //   نگاشت مارکر روی نقشه با توجه به مختصات جغرافیایی وارد شده
  pinMarker(coordinates: Position) {
    if (this.marker) {
      this.marker.remove();
    }
    this.marker = marker(this._positionToLatLng(coordinates), {
      icon: this.markerIcon,
    }).addTo(this.map!);
    this._updatedMarkerCoordinates = this._latLngToPosition(coordinates);
  }

  pinCircleBadge(
    samplingStationPoints: { latitude: number; longitude: number }[],
    service: "EXPERIMENT" | "VISIT"
  ) {
    samplingStationPoints?.map((station, i) => {
      L.marker([station?.latitude, station?.longitude], {
        icon: samplingStationIcon(service, i),
      }).addTo(this.map!);
    });
  }

  mapPolygon(
    coordinates: Position[],
    isEditable: boolean,
    fitBound: boolean,
    productId?: number,
    farmId?: string,
    onFarmClick?: (farmId: string) => void
  ) {
    if (isEditable) {
      this.map?.pm.enableGlobalEditMode(globalEditMode);
      this.map?.pm.setGlobalOptions(globalEditMode);
    }

    const polygonGeoJson = this._coordinatesToGeoJson(coordinates);

    // pmIgnore: true, make it uneditable and unremovable
    const polygon = L.geoJSON(polygonGeoJson, { pmIgnore: !isEditable })
      .setStyle({ color: this._setBorderColor(productId ?? 20) })
      .addTo(this.map!);

    polygon.on("click", (_) => {
      onFarmClick && onFarmClick(farmId || "");
    });

    if (fitBound) {
      this.map?.fitBounds(polygon.getBounds());
    }
  }

  mapChildPolygonsCreatedBySplit(
    coordinates: Position[],
    color: "yellow" | "green"
  ) {
    const polygonGeoJson = this._coordinatesToGeoJson(coordinates);
    const polygon = L.geoJSON(polygonGeoJson)
      .setStyle({ color })
      .addTo(this.map!);

    polygon.on("click", (_) => {
      this.setPolygonDetails(coordinates, "[latitude | longitude]");
      this.pinMarker(this.polygonDetails.centerCoordinates);
    });
  }

  private _positionToLatLng(coordinates: LatLng | Position): LatLng {
    if (coordinates instanceof LatLng) {
      return coordinates;
    } else {
      return new LatLng(coordinates[0], coordinates[1]);
    }
  }

  private _latLngToPosition(coordinates: LatLng | Position): Position {
    if (coordinates instanceof LatLng) {
      return [coordinates.lat, coordinates.lng];
    } else {
      return coordinates;
    }
  }

  private _coordinatesToGeoJson(
    // Leaflet uses lat-lng (or northing-easting) whereas GeoJSON uses lng-lat (or easting-northing).
    coordinates: Position[]
  ): GeoJSON.FeatureCollection<any> {
    let arrayTypePolygonCoordinates: Position[] = [];

    arrayTypePolygonCoordinates = coordinates?.map((coord) => [
      (coord as Position)[1],
      (coord as Position)[0],
    ]);

    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [arrayTypePolygonCoordinates],
          },
          properties: {},
        },
      ],
    };
  }

  closePolygon(coordinates: Position[]) {
    if (
      coordinates[0][0] === coordinates[coordinates.length - 1][0] &&
      coordinates[0][1] === coordinates[coordinates.length - 1][1]
    ) {
      return coordinates;
    } else {
      return [...coordinates, [coordinates[0][0], coordinates[0][1]]];
    }
  }

  isPointInPolygon(currentPoint: Position | LatLng, polygon: Position[]) {
    let isPointInPolygon = false;

    const turfPoint = point(this._latLngToPosition(currentPoint));

    const turfPolygon = poly([this.closePolygon(polygon)]);
    if (booleanPointInPolygon(turfPoint, turfPolygon)) {
      isPointInPolygon = true;
    }

    return isPointInPolygon;
  }

  isPolygonInPolygon(currentPolygon: Position[], toCheckPolygon: Position[]) {
    let isAnotherPolygonInCurrentPolygon = false;

    const turfCurrentPolygon = poly([this.closePolygon(currentPolygon)]);

    const turfToCheckPolygon = poly([this.closePolygon(toCheckPolygon)]);
    if (booleanContains(turfCurrentPolygon, turfToCheckPolygon)) {
      isAnotherPolygonInCurrentPolygon = true;
    }

    return isAnotherPolygonInCurrentPolygon;
  }

  isPolygonIntersectedPolygon(currentPolygon: Position[], polygon: Position[]) {
    let isPolygonIntersected = false;

    const turfCurrentPolygon = poly([this.closePolygon(currentPolygon)]);

    const turfPolygon = poly([this.closePolygon(polygon)]);

    if (intersect(turfCurrentPolygon, turfPolygon)) {
      isPolygonIntersected = true;
    }

    return isPolygonIntersected;
  }

  isPolygonWithinPolygon(currentPolygon: Position[], polygon: Position[]) {
    let isPolygonWithinPolygon = false;

    const turfCurrentPolygon = poly([this.closePolygon(currentPolygon)]);

    const turfPolygon = poly([this.closePolygon(polygon)]);

    if (booleanWithin(turfCurrentPolygon, turfPolygon)) {
      isPolygonWithinPolygon = true;
    }

    return isPolygonWithinPolygon;
  }

  isPointOnLine(point: Position, polygon: Position[]) {
    let isPointOnLine = false;

    const turfLine = lineString(polygon);

    if (booleanPointOnLine(point, turfLine)) {
      isPointOnLine = true;
    }

    return isPointOnLine;
  }

  isLineIntersectedPolygon(line: Position[], polygon: Position[]) {
    let isPolygonIntersected = false;

    if (line.length > 1) {
      const turfLineString = lineString(line);
      const turfPolygon = poly([this.closePolygon(polygon)]);

      if (lineIntersect(turfLineString, turfPolygon).features.length > 0) {
        isPolygonIntersected = true;
      }
    }

    return isPolygonIntersected;
  }

  polygonsDifference(
    currentPolygon: Position[],
    toDiffPolygons: Position[][]
  ): Position[] | null {
    const turfCurrentPolygon = poly([this.closePolygon(currentPolygon)]);

    const currentPolygonWithoutChilds: Feature<Polygon, Properties> | null =
      toDiffPolygons
        .map((toDiffPolygon) => this.closePolygon(toDiffPolygon))
        .reduce(
          (
            currentPolygonWithoutInnerPolygons: Feature<
              Polygon,
              Properties
            > | null,
            childPolygon
          ) => {
            const diffArea = currentPolygonWithoutInnerPolygons
              ? difference(
                  currentPolygonWithoutInnerPolygons,
                  poly([childPolygon])
                )
              : null;
            if (diffArea) {
              if (diffArea.geometry.type === "Polygon") {
                const diffAreaFeature: Feature<Polygon, Properties> = {
                  type: "Feature",
                  properties: diffArea.properties,
                  geometry: {
                    type: "Polygon",
                    coordinates: diffArea.geometry.coordinates as Position[][],
                  },
                };
                return diffAreaFeature;
              } else if (diffArea.geometry.type === "MultiPolygon") {
                const diffAreaFeature: Feature<Polygon, Properties> = {
                  type: "Feature",
                  properties: diffArea.properties,
                  geometry: {
                    type: "Polygon",
                    coordinates: (
                      diffArea.geometry.coordinates as Position[][][]
                    )[0],
                  },
                };
                return diffAreaFeature;
              } else {
                return null;
              }
            } else {
              return null;
            }
          },
          turfCurrentPolygon
        );

    return currentPolygonWithoutChilds
      ? currentPolygonWithoutChilds.geometry.coordinates[0]
      : null;
  }

  bufferPolygon(polygon: Position[], bufferSize: number) {
    const turfPolygon = poly([this.closePolygon(polygon)]);
    const expandedPolygon = buffer(turfPolygon, bufferSize, {
      units: "centimeters",
    });
    return expandedPolygon.geometry.coordinates[0];
  }

  protected _coordinatesToPolygonData(
    coordinates: Position[],
    coordinateShape: CoordinateShape
  ) {
    const polygonCenter = centroid(polygon([coordinates])).geometry.coordinates;

    const latLngLikeCoordinates = coordinates.map((coor) =>
      coordinateShape === "[latitude | longitude]"
        ? new LatLng(coor[0], coor[1])
        : new LatLng(coor[1], coor[0])
    );

    latLngLikeCoordinates.pop();
    const area = computeArea(latLngLikeCoordinates);
    const areaHectare = (area / 10000).toFixed(2);

    return {
      polygon:
        coordinateShape === "[latitude | longitude]"
          ? coordinates
          : coordinates.map((coordinate) => [coordinate[1], coordinate[0]]),
      centerCoordinates:
        coordinateShape === "[latitude | longitude]"
          ? [polygonCenter[0], polygonCenter[1]]
          : [polygonCenter[1], polygonCenter[0]],
      area: +areaHectare,
    };
  }

  setPolygonDetails(coordinates: Position[], coordinateShape: CoordinateShape) {
    const { polygon, centerCoordinates, area } = this._coordinatesToPolygonData(
      coordinates,
      coordinateShape
    );
    this.setPopup({ area });

    this.polygonDetails = {
      polygon,
      centerCoordinates,
      area: +area,
    };
  }

  setPopup({ area, position }: { area?: number; position?: Position }) {
    const popUpContent = area
      ? areaCalculation(area)
      : `${position![1].toFixed(6)} - ${position![0].toFixed(6)}`;

    const popupElement = this.popupRef.current;
    popupElement!.setAttribute("style", "display:flex");
    popupElement!.childNodes[0].textContent = popUpContent;
  }

  layerToCoordinates(layer: Layer) {
    // @ts-ignore
    const feature = layer.toGeoJSON(15);
    let coordinates = feature.geometry.coordinates[0];
    return coordinates;
  }

  lineToCoordinates(layer: any) {
    const feature = layer.toGeoJSON(15);
    let coordinates = feature.geometry.coordinates;
    return coordinates;
  }

  private _setBorderColor = (productId: number) => {
    return productColors[`${productId}`];
  };

  validationResult(isValid: boolean = true, message: string = ""): Validation {
    return {
      isValid,
      message,
    };
  }
}
